// @utilities
const apiURLBuilder = require('core/apiURLBuilder');
const { APIGet, APIPost } = require('utilities/APIRequestHelper');
const {
    recordProcess,
    PROC_END,
    PROC_FAIL,
    PROC_RA_MODIFY_STRATEGY,
} = require('utilities/tagging');

// @constants
const actionTypes = require('../raConstants/actionTypes');
const errorActions = require('actions/brokerError');

// @actions
const { updateSimulation } = require('./RASimulation');
const raErrorHandlerActions = require('./RAErrorHandler');

const setInitialContributionAmount = amount => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_SET_INITIAL_CONTRIBUTION_AMOUNT,
    payload: {
        amount,
    },
});

const setRecurringContributionAmount = amount => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_SET_RECURRING_CONTRIBUTION_AMOUNT,
    payload: {
        amount,
    },
});

const setPeriodicity = periodicity => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_SET_PERIODICITY,
    payload: {
        periodicity,
    },
});

const setContributionDay = day => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_SET_CONTRIBUTION_DAY,
    payload: {
        day,
    },
});

const setCustomName = name => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_SET_CUSTOM_NAME,
    payload: {
        name,
    },
});

const selectStrategy = value => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_SELECT_STRATEGY,
    payload: {
        value,
    },
});

const modifyStrategy = (
    currentProfile,
    newProfile,
    originalPortfolio,
) => dispatch => {
    if (currentProfile) {
        dispatch({
            type: actionTypes.RA_MANAGEMENT_PLAN_MODIFY_STRATEGY_AND_PROFILE,
            payload: {
                investProfileValue: currentProfile,
                newProfile,
                originalPortfolio,
            },
        });
    } else {
        dispatch({
            type: actionTypes.RA_MANAGEMENT_PLAN_MODIFY_STRATEGY,
            payload: {
                newProfile,
                originalPortfolio,
            },
        });
    }
};

const showDetails = () => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_SHOW_DETAILS,
});

const reset = () => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_RESET,
});

const requestSimulationInProgress = () => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_GET_INVESTMENT_PROFILE_REQUEST,
});

const buildEmptySimulation = () => ({
    amount: 0,
    initialContribution: 0,
    selectedPortfolio: '6',
    maxTypeOfProduct: 1,
    hasRecurringTransfer: false,
    endDate: '',
    name: '',
    type: '',
    id: '001',
    error: true,
});

const requestSimulationSuccess = data => {
    const formattedData = !!data ? data : buildEmptySimulation();
    Object.keys(formattedData).forEach(
        key => formattedData[key] == null && delete formattedData[key],
    );
    return {
        type: actionTypes.RA_MANAGEMENT_PLAN_GET_INVESTMENT_PROFILE_SUCCESS,
        payload: {
            data: formattedData,
        },
    };
};

const requestSimulationFailure = error => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_GET_INVESTMENT_PROFILE_FAILURE,
    payload: {
        error,
    },
});

const requestSimulation = (pureRobo = false) => dispatch => {
    dispatch(requestSimulationInProgress());

    const requestData = {
        header: {
            version: !__CONTACT_CENTER__ ? 2 : undefined,
        },
        query: {
            pureRobo: pureRobo,
        },
    };

    return APIGet(dispatch, apiURLBuilder.getURL('raSimulation'), requestData)
        .then(response => dispatch(requestSimulationSuccess(response)))
        .catch(error => {
            if (error.error && error.error.status == 404) {
                dispatch(requestSimulationSuccess(buildEmptySimulation()));
            } else {
                dispatch(requestSimulationFailure(error));
            }
        });
};

const saveSimulationInProgress = () => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_SAVE_SIMULATION_REQUEST,
});

const saveSimulationSuccess = data => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_SAVE_SIMULATION_SUCCESS,
    payload: {
        data,
    },
});

const saveSimulationFailure = error => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_SAVE_SIMULATION_FAILURE,
    payload: {
        error,
    },
});

const saveSimulation = (history, gbi = false) => (dispatch, getState) => {
    const immGoal = getState().raCustomPlanByObjective;
    const immSimulation = gbi ? immGoal : getState().raManagementPlan;
    const cutDaty = gbi
        ? immSimulation.get('contributionDay')
        : immSimulation.get('recurringContributionDay');
    const amount = immGoal.get('objectiveAmount') || undefined;
    const endDate = immGoal.get('objectiveEndDate') || undefined;
    const type =
        immGoal.get('objectiveType') &&
        immGoal.get('objectiveType').toUpperCase();
    const initialContribution =
        immSimulation.get('initialContributionAmount') || undefined;
    const name = immGoal.get('objective');
    const displayName = immSimulation.get('portfolioCustomName');
    const selectedPortfolio = gbi
        ? immSimulation.get('selectedStrategy')
        : immSimulation.get('investProfileValue');
    const isSavingSimulation = immSimulation.get('isSavingSimulation');
    let requestData = {};
    let recurringTransfer = immSimulation.get('recurringContributionAmount')
        ? {
              amount: immSimulation.get('recurringContributionAmount'),
              cutDay: cutDaty,
              frequencyCode: immSimulation.get('periodicity'),
          }
        : undefined;
    const update = {
        amount,
        currency: 'EUR',
        endDate,
        initialContribution,
        name,
        displayName,
        recurringTransfer,
        selectedPortfolio,
        type,
    };

    if (isSavingSimulation) {
        return;
    }

    dispatch(saveSimulationInProgress());
    dispatch(updateSimulation(update));

    const simulation = getState()
        .raSimulation.get('simulation')
        .toJS();
    simulation.pureRobo = !gbi;
    requestData = {
        header: {
            version: 2,
        },
        query: simulation,
    };

    return APIPost(dispatch, apiURLBuilder.getURL('raSimulation'), requestData)
        .then(response => dispatch(saveSimulationSuccess(response)))
        .catch(error => {
            if (error.error) {
                dispatch(saveSimulationFailure(error.error.description));
            } else {
                throw error;
            }
            raErrorHandlerActions.show(error, history);
        });
};

const modifyProfile = newProfile => (dispatch, getState) => {
    const immPortfolioDistribution = getState().raPortfolioDistribution;
    dispatch({
        type: actionTypes.RA_MANAGEMENT_PLAN_MODIFY_PROFILE,
        payload: {
            investProfileValue: immPortfolioDistribution.getIn([
                'roboAdvisor',
                'product',
                'typeOfProduct',
            ]),
            newProfile,
        },
    });
};

const setInclutionGoal = () => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_INCLUDE_GOAL,
});

const setEditClicked = () => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_SET_EDIT_CLICKED,
});

const changeProductRequest = query => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_CHANGE_PRODUCT_REQUEST,
    payload: { query },
});

const changeProductSuccess = () => dispatch => {
    dispatch({ type: actionTypes.RA_MANAGEMENT_PLAN_CHANGE_PRODUCT_SUCCESS });
};

const changeProductError = error => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_CHANGE_PRODUCT_ERROR,
    payload: { error },
});

const changeProduct = (query, history) => dispatch => {
    dispatch(changeProductRequest());
    const customData = {
        processDetail: query.typeOfProduct,
    };

    return APIPost(dispatch, apiURLBuilder.getURL('raChangeProduct'), { query })
        .then(() => {
            dispatch(changeProductSuccess());
            recordProcess(PROC_RA_MODIFY_STRATEGY, PROC_END, customData);
        })
        .catch(error => {
            if (error.error) {
                dispatch(changeProductError(error));
            }
            dispatch(raErrorHandlerActions.show(error, history));
            recordProcess(PROC_RA_MODIFY_STRATEGY, PROC_FAIL, customData);
        });
};

const saveSimulationChangeModalOption = value => ({
    type: actionTypes.RA_SAVE_SIMULATION_CHANGE_MODAL_OPTION,
    payload: { value },
});

const resetValueOfSaveSimulation = () => ({
    type: actionTypes.RA_SAVE_SIMULATION_RESET,
});

const disableSaveSimulationButton = value => ({
    type: actionTypes.RA_SAVE_SIMULATION_DISABLE_SIMULATION_BUTTON,
    payload: { value },
});

const updateCustomName = value => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_UPDATE_CUSTOM_NAME,
    payload: {
        value,
    },
});

const resetScrollHeight = () => ({
    type: actionTypes.RA_SIMULATION_RESET_SCROLL_HEIGHT,
});

const setScrollHeight = () => ({
    type: actionTypes.RA_SIMULATION_SET_SCROLL_HEIGHT,
});

const fetchingValidateBlockedAccount = state => ({
    type: actionTypes.RA_MANAGEMENT_PLAN_FETCHING_VALIDATE_BLOCKED_ACCOUNTS,
    payload: state,
});

const validateBlockedAccount = history => (dispatch, getState) => {
    const accountsById = getState().accounts.get('byId');
    const contracto = accountsById.map(account => {
        return {
            numerodecontrato: account.get('newContractNumber'),
            producto: account.get('newProduct'),
        };
    });
    const requestData = {
        query: {
            accounts: Object.values(contracto.toJS()),
        },
    };
    dispatch(fetchingValidateBlockedAccount(true));
    return APIPost({
        dispatch,
        endpoint: 'validateBlockedAccount',
        params: requestData,
    })
        .then(() => {
            dispatch(fetchingValidateBlockedAccount(false));
        })
        .catch(error => {
            dispatch(fetchingValidateBlockedAccount(false));
            const errorKey = error.error.error;
            const errorStatus = error.error.status;
            if (errorStatus !== 412) {
                return;
            }
            dispatch(
                errorActions.show('brokerAPIErrors-' + errorKey, true, history),
            );
        });
};

module.exports = {
    changeProduct,
    disableSaveSimulationButton,
    modifyProfile,
    modifyStrategy,
    requestSimulation,
    reset,
    resetScrollHeight,
    resetValueOfSaveSimulation,
    saveSimulation,
    saveSimulationChangeModalOption,
    selectStrategy,
    setContributionDay,
    setCustomName,
    setEditClicked,
    setInitialContributionAmount,
    setInclutionGoal,
    setPeriodicity,
    setRecurringContributionAmount,
    showDetails,
    updateCustomName,
    setScrollHeight,
    validateBlockedAccount,
};
